"use client";

import React, { useRef, useState } from "react";
import {
  markerPopupOffset,
  RMap,
  RNavigationControl,
  RPopup,
} from "maplibre-react-components";
import type {
  LightSpecification,
  LngLatLike,
  Map as MapLibre,
  MapLibreEvent,
} from "maplibre-gl";
import {
  MissionMapLibreLayer,
  SiteMapLibreGeoJSONLayer,
  SiteMapLibrePopup,
  SiteMapMarker,
} from "@/components";
import { FeatureCollection } from "geojson";
import { MissionType } from "@/types";
import { useAtomValue } from "jotai";
import { mapProjectionAtom, mapTileAtom } from "@/store";
import { MapProjection } from "@oreme/ui";
import { useMission, useSite } from "@/hooks";

interface MapProps {
  sites: FeatureCollection;
  missions?: MissionType[];
}

function SharedMap({ sites, missions = [] }: Readonly<MapProps>) {
  const mapRef = useRef<MapLibre>(null);
  const tile = useAtomValue(mapTileAtom);
  const { mission } = useMission();
  const { site } = useSite();
  const [isLoaded, setIsLoaded] = useState(false);
  const center: LngLatLike = [3.8593521385608796, 43.62179360643451];
  const zoom = 2;

  const mapProjection: MapProjection = useAtomValue(mapProjectionAtom);
  const mapLight: LightSpecification = {
    anchor: "viewport",
    color: "white",
    intensity: 0.4,
  };

  const handleLoad = (e: MapLibreEvent) => {
    const map = e.target;
    map.setProjection({
      type: mapProjection.id,
    });
    map.setLight(mapLight);
    setIsLoaded(true);
  };

  return (
    <RMap
      className={`${isLoaded ? "animate__animated animate__fadeIn animate__faster" : "hidden"} bg-accent circle-grid-pattern`}
      id="map"
      initialCenter={center}
      initialZoom={zoom}
      mapStyle={tile.url}
      ref={mapRef}
      onLoad={handleLoad}
      initialAttributionControl={{ compact: true }}
    >
      {site ? (
        <RPopup
          latitude={site.latitude}
          longitude={site.longitude}
          offset={markerPopupOffset}
        >
          <SiteMapLibrePopup site={site} missions={missions} />
          <SiteMapMarker site={site} />
        </RPopup>
      ) : null}
      <RNavigationControl position="top-right" visualizePitch />
      {mission ? <MissionMapLibreLayer mission={mission} /> : null}
      <SiteMapLibreGeoJSONLayer data={sites} />
    </RMap>
  );
}

export { SharedMap };
